// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// COB-457: the mandatory key-backup gate on the shared-vault consent
// surfaces.
//
// In a peer-to-peer m-of-n vault a participant losing their key is not a
// private loss — it degrades the whole vault's quorum, and past a threshold it
// freezes everyone's funds. Backup was advisory everywhere on the accept path,
// so joining is now a precondition-checked act: the consent affordance stays
// locked until this browser's key backup is confirmed, and an unbacked caller
// is walked through the ceremony IN PLACE.
//
// In place is load-bearing, not a nicety. The email-link accept page holds the
// single-use invite token in memory only (as it does the JWT — see
// `feedback_user_app_jwt_in_memory_not_cookie`), so any navigation away — a
// "back up your key in the wallet and come back" link — destroys the
// invitation. Everything here therefore renders inside the consent surface.
//
// Per agent kind (`session.agentKind`, never sniffed from agent behaviour):
//
//   * `software`  — the real IndexedDB answer, and the ceremony can remediate.
//                   Note `restoreSeed` also marks the backup confirmed and
//                   `completeNewAccount` calls `confirmBackup`, so an
//                   authenticated software session is almost always already
//                   confirmed: this gate is fail-closed invariant enforcement
//                   over a rare/legacy unbacked population, not a new step for
//                   the common path.
//   * `hardware`  — always confirmed. The device's recovery phrase was
//                   confirmed at device setup; `hardware-agent-client.ts`
//                   answers `isBackupConfirmed()` with `true` for exactly this
//                   reason.
//   * `web3auth`  — EXEMPT, and never probed. There is no seed, and the worker
//                   entry's `requireSoftware()` makes `isBackupConfirmed()`
//                   REJECT — a uniform call would blow up the gate. Delegated
//                   custody's loss coverage is the login-loss acknowledgement
//                   (COB-272) plus the activation key-backup acknowledgement
//                   (COB-457 leg 1).
//
// Fail-closed throughout: an unknown, pending, or rejected probe never opens
// the gate, and the render gate is backed by a handler-side guard at every
// consent call site.

import { useCallback, useEffect, useState } from 'preact/hooks';
import { useSession, type AgentKind } from '../session-context.js';
import type { KeyAgentClient } from '../key-agent-worker-client.js';
import { PasswordManagerUsername } from './password-manager-username.js';
import { SEED_LOSS_ACK_LABEL } from '../pages/onboarding.js';
// The reveal budget and the passcode-challenge copy are the wallet's
// `ExportSeedCard` primitives (the house post-onboarding reveal pattern);
// imported rather than restated so there is one source of truth for both.
import { SEED_REVEAL_TIMEOUT_MS, revealFailureMessage } from '../pages/wallet.js';
import '../styles/accept.css';
import '../styles/shared-vaults.css';

// ── Status model ──────────────────────────────────────────────────────

/** Where a consent surface stands on this session's key backup.
 *
 *  `unknown` covers both "not probed yet" and "no agent" — it is a pending
 *  state, never a permissive one. Only `confirmed` opens the gate. */
export type KeyBackupStatus = 'unknown' | 'confirmed' | 'unconfirmed';

/** Whether this agent kind is probed at all. `web3auth` is exempt (no seed;
 *  `isBackupConfirmed()` rejects), so it must never reach the agent call. */
export function keyBackupProbeApplies(agentKind: AgentKind | null): boolean {
  return agentKind === 'software' || agentKind === 'hardware';
}

/** Whether a consent affordance must stay blocked. Exported so both the render
 *  gate and the handler-side guard read the same predicate. */
export function keyBackupGateBlocks(status: KeyBackupStatus): boolean {
  return status !== 'confirmed';
}

/** The error a consent handler raises when it is invoked while the gate is
 *  not passed — the second line of defence behind the render gate. */
export const KEY_BACKUP_GATE_BLOCKED_ERROR =
  'Back up your recovery phrase before you consent to this vault.';

/** One probe of the agent's backup flag, resolved into a status.
 *
 *  Every failure mode collapses to `unconfirmed`: a rejection (a web3auth
 *  agent reached by mistake, a worker that died, an IndexedDB fault) must
 *  never skip the gate. Exported for unit tests. */
export async function readKeyBackupStatus(
  agent: KeyAgentClient | null,
  agentKind: AgentKind | null,
): Promise<KeyBackupStatus> {
  if (!agent || agentKind === null) return 'unknown';
  if (!keyBackupProbeApplies(agentKind)) return 'confirmed';
  try {
    return (await agent.isBackupConfirmed()) ? 'confirmed' : 'unconfirmed';
  } catch {
    return 'unconfirmed';
  }
}

export interface KeyBackupGate {
  status: KeyBackupStatus;
  /** True iff the consent affordance may fire. */
  satisfied: boolean;
  /** Re-probe the agent — the ceremony calls this after `confirmBackup()` so
   *  the unlock is driven by a fresh answer, not by an optimistic flip. */
  refresh: () => void;
}

/** The gate hook. One implementation, used by the claim-link accept page —
 *  which is the only consent surface since COB-543 retired the in-app
 *  pending-invitee section along with the tokenless accept it served. */
export function useKeyBackupGate(): KeyBackupGate {
  const session = useSession();
  const { agent, agentKind } = session;
  const [status, setStatus] = useState<KeyBackupStatus>('unknown');
  const [probeNonce, setProbeNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!agent || agentKind === null) {
      setStatus('unknown');
      return;
    }
    if (!keyBackupProbeApplies(agentKind)) {
      setStatus('confirmed');
      return;
    }
    // Reset before every probe (including a `refresh`) so a previous
    // identity's answer can never linger as a permissive state.
    setStatus('unknown');
    void readKeyBackupStatus(agent, agentKind).then((next) => {
      if (!cancelled) setStatus(next);
    });
    return () => {
      cancelled = true;
    };
  }, [agent, agentKind, probeNonce]);

  const refresh = useCallback(() => setProbeNonce((n) => n + 1), []);

  return { status, satisfied: !keyBackupGateBlocks(status), refresh };
}

// ── In-place ceremony ─────────────────────────────────────────────────

/** Why the consent affordance is missing — one sentence, in the accept
 *  page's own voice. */
export const KEY_BACKUP_GATE_NOTE =
  'Back up your recovery phrase before you consent — if you lose your key, ' +
  'your co-signers may never be able to form a signing quorum again.';

type CeremonyMode = 'idle' | 'prompt' | 'verifying' | 'revealed' | 'confirming';

/**
 * The in-place remediation: passcode re-challenge → reveal the 24 words →
 * loss acknowledgement → `confirmBackup()`. Rendered instead of the consent
 * button on both surfaces, and it never routes anywhere (see the module note
 * on the in-memory invite token).
 *
 * Deliberately NOT a word-verify quiz. Onboarding's 3-word check exists to
 * catch a user who skipped writing the phrase down at the moment the seed is
 * first created; this ceremony is invariant repair for an already-live
 * account whose seed is already on the device, and it is transcribed under
 * the same 60s reveal budget the wallet's export card gives. Scope containment,
 * recorded here so it reads as a decision rather than an omission.
 *
 * `onConfirmed` re-probes the agent — the gate never flips on this
 * component's say-so.
 */
export function KeyBackupCeremony({ onConfirmed }: { onConfirmed: () => void }) {
  const session = useSession();
  const [mode, setMode] = useState<CeremonyMode>('idle');
  const [passcode, setPasscode] = useState('');
  const [seed, setSeed] = useState<string[] | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrub = useCallback(() => {
    setSeed(null);
    setPasscode('');
    setAcknowledged(false);
    setMode('idle');
  }, []);

  // Auto-hide the revealed phrase on the wallet card's budget.
  useEffect(() => {
    if (mode !== 'revealed') return;
    const t = setTimeout(scrub, SEED_REVEAL_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [mode, scrub]);

  // Scrub if the session locks or expires from underneath the reveal.
  useEffect(() => {
    if (!session.isAuthenticated && mode !== 'idle') scrub();
  }, [session.isAuthenticated, mode, scrub]);

  async function verifyAndReveal(e?: Event) {
    if (e) e.preventDefault();
    setError(null);
    if (!session.agent) {
      setError('Session expired. Unlock again.');
      return;
    }
    if (!session.userEmail) {
      setError('Session email unavailable. Please log out and back in.');
      return;
    }
    if (passcode.length === 0) {
      setError('Enter your passcode.');
      return;
    }
    setMode('verifying');
    try {
      const status = await session.passcodeCheck(session.userEmail, passcode);
      if (status !== 'match') {
        setError(revealFailureMessage(status as 'mismatch' | 'no_user'));
        setPasscode('');
        setMode('prompt');
        return;
      }
      const mnemonic = await session.agent.exportSeed();
      setSeed(mnemonic.split(' '));
      setPasscode('');
      setMode('revealed');
    } catch (err) {
      setError(revealFailureMessage('unknown', err));
      setPasscode('');
      setMode('prompt');
    }
  }

  async function confirm() {
    if (!session.agent) {
      setError('Session expired. Unlock again.');
      return;
    }
    setError(null);
    setMode('confirming');
    try {
      await session.agent.confirmBackup();
      setSeed(null);
      setAcknowledged(false);
      // The parent re-probes; a still-unconfirmed answer re-mounts this
      // component from scratch rather than leaving a half-finished ceremony.
      onConfirmed();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not record your backup. Please try again.',
      );
      setMode('revealed');
    }
  }

  return (
    <div class="sv-key-backup-gate" data-testid="sv-key-backup-gate">
      <h3 class="shared-vault-section-title">
        Back up your recovery phrase first
      </h3>
      <div class="seed-warning">{KEY_BACKUP_GATE_NOTE}</div>
      {error && <p class="accept-error">{error}</p>}

      {mode === 'idle' && (
        <button
          type="button"
          class="btn btn-primary"
          onClick={() => {
            setError(null);
            setPasscode('');
            setMode('prompt');
          }}
        >
          Show recovery phrase
        </button>
      )}

      {(mode === 'prompt' || mode === 'verifying') && (
        <form class="seed-challenge" onSubmit={verifyAndReveal}>
          <p class="accept-input-hint">
            Re-enter your passcode to reveal your 24-word recovery phrase. It
            will hide itself after a minute.
          </p>
          {/* COB-502: username companion — this form submits, so a password
              manager reads it as a credential save; without it the entry it
              writes for the origin has a blank username. */}
          {session.userEmail && (
            <PasswordManagerUsername
              id="cobault-backup-gate-username"
              email={session.userEmail}
            />
          )}
          <div class="accept-field">
            <label class="accept-label" for="cobault-backup-gate-passcode">
              Passcode
            </label>
            <input
              id="cobault-backup-gate-passcode"
              name="passcode"
              class="accept-input"
              type="password"
              autocomplete="current-password"
              value={passcode}
              disabled={mode === 'verifying'}
              onInput={(e) => setPasscode((e.target as HTMLInputElement).value)}
              onChange={(e) => setPasscode((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="seed-challenge-actions">
            <button
              type="submit"
              class="btn btn-primary"
              disabled={mode === 'verifying' || passcode.length === 0}
            >
              {mode === 'verifying' ? 'Verifying…' : 'Reveal'}
            </button>
            <button
              type="button"
              class="btn btn-secondary"
              onClick={scrub}
              disabled={mode === 'verifying'}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {(mode === 'revealed' || mode === 'confirming') && seed && (
        <div>
          <div class="seed-warning">
            Write these 24 words down in order and keep them offline. Make sure
            no one is watching your screen — this hides itself automatically.
          </div>
          <div class="seed-grid">
            {seed.map((word, i) => (
              <div key={i} class="seed-word">
                <span class="seed-word-num">{i + 1}</span>
                <span class="seed-word-text">{word}</span>
              </div>
            ))}
          </div>
          <label class="loss-ack-row">
            <input
              type="checkbox"
              checked={acknowledged}
              disabled={mode === 'confirming'}
              onChange={(e) =>
                setAcknowledged((e.target as HTMLInputElement).checked)
              }
            />
            <span>{SEED_LOSS_ACK_LABEL}</span>
          </label>
          <button
            type="button"
            class="btn btn-primary"
            disabled={!acknowledged || mode === 'confirming'}
            onClick={() => void confirm()}
          >
            {mode === 'confirming' ? 'Saving…' : "I've written them down"}
          </button>
        </div>
      )}
    </div>
  );
}
